import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { 
  ShoppingCart, 
  Menu, 
  Close,
  Store,
  Person,
  Dashboard,
  Login,
  HowToReg
} from '@mui/icons-material';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useShopContext } from '../../context/ShopContext';
import { useGenericModal } from '../../context/GenericModalContext';
import { useLanguage } from '../../context/LanguageContext';

interface NavbarProps {
  onPreviewToggle: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onPreviewToggle }) => {
  const { cartItems } = useCart();
  const { user, logout } = useAuth();
  const { openModal } = useGenericModal();
  const { loading: shopLoading, selectedStore } = useShopContext();
  const { language, toggleLanguage, t } = useLanguage();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isStoreMenuOpen, setIsStoreMenuOpen] = useState(false);

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    setIsStoreMenuOpen(false);
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const toggleStoreMenu = () => setIsStoreMenuOpen((prev) => !prev);

  // ✅ navigation links config
  const navLinks = [
    { 
      to: '/products', 
      label: t('Products', 'المنتجات'),
      icon: <Store className="w-4 h-4 mr-2" />
    },
    { 
      to: '/profile', 
      label: t('Profile', 'الملف الشخصي'), 
      auth: true,
      icon: <Person className="w-4 h-4 mr-2" />
    },
    { 
      to: '/admin', 
      label: t('Admin', 'لوحة التحكم'), 
      roles: ['super_admin', 'branch_owner'],
      icon: <Dashboard className="w-4 h-4 mr-2" />
    },
    { 
      to: '/login', 
      label: t('Login', 'تسجيل الدخول'), 
      guestOnly: true,
      icon: <Login className="w-4 h-4 mr-2" />
    },
    { 
      to: '/register', 
      label: t('Register', 'تسجيل'), 
      guestOnly: true,
      icon: <HowToReg className="w-4 h-4 mr-2" />
    },
  ];

  // ✅ helper to check if a link should render
  const canShowLink = (
    auth?: boolean,
    guestOnly?: boolean,
    roles?: string[] | string
  ) => {
    if (auth && !user) return false;
    if (guestOnly && user) return false;

    if (roles) {
      const allowedRoles = Array.isArray(roles) ? roles : [roles];
      if (!allowedRoles.includes(user?.role ?? "")) return false;
    }

    return true;
  };

  return (
    <nav className="bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-xl sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo/Brand */}
          <NavLink 
            to="/" 
            className="flex items-center gap-2 text-2xl font-bold hover:opacity-90 transition-opacity"
          >
            <div className="bg-gradient-to-r from-primary to-primary-dark w-8 h-8 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">B</span>
            </div>
            <span>{t('bazaar', 'بازار')}</span>
          </NavLink>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {/* Navigation Links */}
            {navLinks.map(({ to, label, auth, guestOnly, roles, icon }) =>
              canShowLink(auth, guestOnly, roles) ? (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    twMerge(
                      'flex items-center px-3 py-2 rounded-lg transition-all duration-200 hover:bg-white/10',
                      isActive ? 'bg-primary/20 text-primary font-semibold' : 'hover:text-gray-300'
                    )
                  }
                >
                  {icon}
                  {label}
                </NavLink>
              ) : null
            )}

            {/* Cart Button */}
            <button
              onClick={onPreviewToggle}
              className="relative flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 hover:text-gray-300 transition-all duration-200 group"
              aria-label={t(`Cart with ${cartCount} items`, `عربة بها ${cartCount} عنصر`)}
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="hidden lg:inline">{t('Cart', 'عربة')}</span>
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                  {cartCount}
                </span>
              )}
            </button>

            {/* User Actions */}
            {user ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                    {user.first_name?.charAt(0) || user.username?.charAt(0) || 'U'}
                  </div>
                  <span className="hidden lg:inline text-sm font-medium">
                    {user.first_name || user.username}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 rounded-lg hover:bg-white/10 hover:text-gray-300 transition-all duration-200"
                  aria-label={t('Logout', 'تسجيل الخروج')}
                >
                  {t('Logout', 'تسجيل الخروج')}
                </button>
              </div>
            ) : null}

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="px-3 py-2 rounded-lg hover:bg-white/10 hover:text-gray-300 transition-all duration-200 font-medium flex items-center gap-2"
            >
              <span className="w-6 h-6 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-xs font-bold">
                {language === 'en' ? 'EN' : 'AR'}
              </span>
              <span className="hidden lg:inline">
                {language === 'en' ? 'العربية' : 'English'}
              </span>
            </button>
          </div>

          {/* Mobile Navigation Toggle */}
          <div className="md:hidden flex items-center gap-3">
            {/* Cart Button Mobile */}
            <button
              onClick={onPreviewToggle}
              className="relative p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
              aria-label={t(`Cart with ${cartCount} items`, `عربة بها ${cartCount} عنصر`)}
            >
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Language Toggle Mobile */}
            <button
              onClick={toggleLanguage}
              className="p-2 rounded-lg hover:bg-white/10 transition-all duration-200 font-medium"
            >
              <span className="w-6 h-6 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-xs font-bold">
                {language === 'en' ? 'EN' : 'AR'}
              </span>
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
              aria-label={isMobileMenuOpen ? t('Close menu', 'إغلاق القائمة') : t('Open menu', 'فتح القائمة')}
            >
              {isMobileMenuOpen ? (
                <Close className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Store Profile Link for Admins/Owners */}
        {selectedStore && ['super_admin', 'branch_owner'].includes(user?.role ?? '') && (
          <div className="mt-3 hidden md:flex justify-center">
            <Link
              to={`/store/${selectedStore.slug || selectedStore.id}/`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/20 to-primary/10 text-primary rounded-lg hover:from-primary/30 hover:to-primary/20 transition-all duration-200 text-sm font-medium border border-primary/20"
            >
              <Store className="w-4 h-4" />
              {t('Store Profile', 'بروفايل المتجر')}: {selectedStore.name}
            </Link>
          </div>
        )}
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="md:hidden bg-gradient-to-b from-gray-900 to-gray-800 text-white border-t border-gray-700"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            role="dialog"
            aria-labelledby="mobile-menu"
          >
            <div className="px-4 py-3 space-y-1">
              {/* Navigation Links */}
              {navLinks.map(({ to, label, auth, guestOnly, roles, icon }) =>
                canShowLink(auth, guestOnly, roles) ? (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      twMerge(
                        'flex items-center px-3 py-3 rounded-lg transition-all duration-200',
                        isActive 
                          ? 'bg-primary/20 text-primary font-semibold' 
                          : 'hover:bg-white/10 hover:text-gray-300'
                      )
                    }
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {icon}
                    {label}
                  </NavLink>
                ) : null
              )}

              {/* Store Profile Mobile */}
              {selectedStore && ['super_admin', 'branch_owner'].includes(user?.role ?? '') && (
                <Link
                  to={`/store/${selectedStore.slug || selectedStore.id}/`}
                  className="flex items-center px-3 py-3 rounded-lg hover:bg-white/10 hover:text-gray-300 transition-all duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Store className="w-4 h-4 mr-2" />
                  {t('Store Profile', 'بروفايل المتجر')}
                </Link>
              )}

              {/* User Info Mobile */}
              {user && (
                <div className="px-3 py-3 border-t border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                      {user.first_name?.charAt(0) || user.username?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="font-medium">{user.first_name || user.username}</p>
                      <p className="text-sm text-gray-400">{user.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center px-3 py-2 bg-gradient-to-r from-red-500/20 to-red-500/10 text-red-400 rounded-lg hover:from-red-500/30 hover:to-red-500/20 transition-all duration-200 font-medium"
                  >
                    {t('Logout', 'تسجيل الخروج')}
                  </button>
                </div>
              )}

              {/* Language Toggle Mobile Full */}
              <div className="px-3 py-3 border-t border-gray-700">
                <button
                  onClick={() => {
                    toggleLanguage();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-500/20 to-orange-500/10 text-amber-400 rounded-lg hover:from-amber-500/30 hover:to-orange-500/20 transition-all duration-200 font-medium"
                >
                  <span className="w-6 h-6 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-xs font-bold">
                    {language === 'en' ? 'EN' : 'AR'}
                  </span>
                  {language === 'en' ? 'العربية' : 'English'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;